import {
  editExercise,
  normalizeComparisonDimension,
  trainingFailure,
} from '../domain/training.ts';
import type {
  ComparisonDimension,
  ExerciseRevision,
} from '../domain/training.ts';
import type {
  TrainingRepository,
  ExerciseView,
  CreateExerciseCommand,
} from '../ports/training-repository.ts';
import { intent } from './training-intent.ts';

export type { ExerciseView } from '../ports/training-repository.ts';

export interface ExerciseFields {
  name: string;
  muscleGroup: string;
  loadStepValue: string;
  loadStepUnit: string;
  variantState?: ComparisonDimension['state'];
  variantValue?: string;
  equipmentState?: ComparisonDimension['state'];
  equipmentValue?: string;
  configurationState?: ComparisonDimension['state'];
  configurationValue?: string;
}

function dimension(
  state: ComparisonDimension['state'] | undefined,
  rawValue: string | undefined,
) {
  const resolved = state ?? 'UNKNOWN';

  if (resolved === 'UNKNOWN') {
    return {
      ok: true as const,
      value: { state: 'UNKNOWN' } as const,
    };
  }

  if (resolved === 'NOT_APPLICABLE') {
    return {
      ok: true as const,
      value: { state: 'NOT_APPLICABLE' } as const,
    };
  }

  if (resolved !== 'KNOWN') {
    return trainingFailure('VALIDATION_ERROR');
  }

  const value = (rawValue ?? '').trim();
  if (!value) return trainingFailure('VALIDATION_ERROR');

  return {
    ok: true as const,
    value: {
      state: 'KNOWN' as const,
      value,
    },
  };
}

function fields(input: ExerciseFields) {
  const {
    name,
    muscleGroup,
    loadStepValue,
    loadStepUnit,
  } = input;

  const variant = dimension(
    input.variantState,
    input.variantValue,
  );
  if (!variant.ok) return variant;

  const equipment = dimension(
    input.equipmentState,
    input.equipmentValue,
  );
  if (!equipment.ok) return equipment;

  const configuration = dimension(
    input.configurationState,
    input.configurationValue,
  );
  if (!configuration.ok) return configuration;

  const comparison = {
    variant: variant.value,
    equipment: equipment.value,
    configuration: configuration.value,
  };

  if (!loadStepValue.trim() && !loadStepUnit.trim()) {
    return {
      ok: true as const,
      value: {
        name,
        muscleGroup,
        ...comparison,
      },
    };
  }

  const number = loadStepValue.trim().replace(',', '.');
  if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(number)) {
    return trainingFailure('VALIDATION_ERROR');
  }

  return {
    ok: true as const,
    value: {
      name,
      muscleGroup,
      ...comparison,
      loadStep: {
        value: Number(number),
        unit: loadStepUnit,
      },
    },
  };
}

type Port = Pick<
  TrainingRepository,
  | 'readContext'
  | 'listExercises'
  | 'readExercise'
  | 'readExerciseRevision'
  | 'createExercise'
  | 'editExercise'
  | 'archiveExercise'
>;

export class TrainingExercises {
  constructor(
    private readonly repository: Port,
    private readonly id: () => string,
  ) {}

  listExercises() {
    return this.repository.listExercises();
  }

  getExercise(id: string) {
    return this.repository.readExercise(id);
  }

  getExerciseRevision(ref: string) {
    return this.repository.readExerciseRevision(ref);
  }

  createExercise(input: ExerciseFields) {
    const draft = structuredClone(input);

    return intent<CreateExerciseCommand, ExerciseView>(
      async () => {
        const context = await this.repository.readContext();
        if (!context.ok) return context;

        const parsed = fields(draft);
        if (!parsed.ok) return parsed;

        const exerciseId = this.id();
        const exerciseRevisionId = this.id();

        return {
          ok: true,
          value: {
            operationId: this.id(),
            context: context.value,
            exercise: {
              exerciseId,
              source: 'CUSTOM',
              archived: false,
              currentRevisionRef: exerciseRevisionId,
            },
            revision: {
              ...parsed.value,
              exerciseId,
              exerciseRevisionId,
              revision: 1,
            },
          },
        };
      },
      command => this.repository.createExercise(command),
      command => this.getExercise(command.exercise.exerciseId),
    );
  }

  editExercise(view: ExerciseView, input: ExerciseFields) {
    const observed = structuredClone(view);
    const draft = structuredClone(input);

    return intent(
      async () => {
        const parsed = fields(draft);
        if (!parsed.ok) return parsed;

        const result = editExercise(
          observed.exercise,
          observed.revision,
          {
            ...parsed.value,
            exerciseRevisionId: this.id(),
            expectedRevision: observed.revision.revision,
          },
        );

        if (!result.ok) return result;

        return {
          ok: true,
          value: {
            operationId: this.id(),
            context: observed.context,
            exerciseId: observed.exercise.exerciseId,
            expectedRevision: observed.revision.revision,
            nextRevision: result.value.revision,
          },
        };
      },
      command => this.repository.editExercise(command),
      command => this.getExercise(command.exerciseId),
    );
  }

  archiveExercise(view: ExerciseView) {
    const observed = structuredClone(view);

    return intent(
      async () => ({
        ok: true,
        value: {
          operationId: this.id(),
          context: observed.context,
          exerciseId: observed.exercise.exerciseId,
          expectedRevision: observed.revision.revision,
        },
      }),
      command => this.repository.archiveExercise(command),
      command => this.getExercise(command.exerciseId),
    );
  }
}

function stateFields(
  dimensionValue: ComparisonDimension | undefined,
) {
  const normalized = normalizeComparisonDimension(dimensionValue);

  return {
    state: normalized.state,
    value: normalized.state === 'KNOWN'
      ? normalized.value
      : '',
  };
}

export function exerciseFields(
  revision?: ExerciseRevision,
): ExerciseFields {
  const variant = stateFields(revision?.variant);
  const equipment = stateFields(revision?.equipment);
  const configuration = stateFields(revision?.configuration);

  return {
    name: revision?.name ?? '',
    muscleGroup: revision?.muscleGroup ?? '',
    loadStepValue: revision?.loadStep
      ? String(revision.loadStep.value)
      : '',
    loadStepUnit: revision?.loadStep?.unit ?? '',
    variantState: variant.state,
    variantValue: variant.value,
    equipmentState: equipment.state,
    equipmentValue: equipment.value,
    configurationState: configuration.state,
    configurationValue: configuration.value,
  };
}
